import type { TrackingIdentity } from '~/core/entities/TrackingIdentity';

import { useVisitorIdentity } from './useVisitorIdentity';
import { getOrCreateSessionId } from './utils';

export type { TrackingIdentity } from '~/core/entities/TrackingIdentity';

/**
 * Returns a getter that builds the identity triplet expected by the upstream
 * API on actions that support attribution (lead creation, wishlist creation,
 * tracking events, etc.).
 *
 * The composable itself must be called from a setup context — it captures the
 * Nuxt-bound dependencies (`useCookie` via `useVisitorIdentity`, `useUserSession`)
 * once. The returned getter is safe to invoke later from async paths or event
 * handlers, where the implicit Nuxt context may have been lost.
 *
 * `sessionId` is re-read on every call so it tracks the 35-min inactivity TTL;
 * `anonymousId` is read once because the cookie has a 2-year lifetime.
 */
export function useTrackingIdentity(): () => TrackingIdentity {
	const { anonymousId } = useVisitorIdentity();
	const { user } = useUserSession();

	return () => ({
		sessionId: getOrCreateSessionId(),
		userId: user.value?.id ?? null,
		anonymousId
	});
}
